import {
    Portal,
    render,
} from 'solid-js/web'
import './styles/main.scss'
import { Router } from '@solidjs/router'
import {
    ErrorBoundary,
    onMount,
    For,
    Show,
    createEffect,
    createResource,
    createSignal,
    getOwner,
    lazy,
    on,
    onCleanup,
    runWithOwner,
} from 'solid-js'
import { IntlProvider } from '@cookbook/solid-intl'
import errorStore from './stores/error-store.js'
import ErrorModal from './app/modals/error-modal/error-modal.jsx'
import PublicTemplateComponent from './app/components/templates/public-template/public-template-component.jsx'
import { useAuthSync } from './app/routes/router-utils.js'
import {
    initGtag,
    initMetaPixel,
    initRedditPixel,
} from './utilities/use-analytics.js'
import {
    captureError,
    initSentry,
} from './utilities/use-sentry.js'
import {
    decodeJwtPayload,
    identify as posthogIdentify,
    initPostHog,
    personPropertiesFromUser,
    setPersonProperties as posthogSetPersonProperties,
} from './utilities/use-posthog.js'
import {
    ensureLodgrExtensionInstallationDetected,
    requestExtensionAuth,
} from './app/utils/extension-installation.js'
import { runWhenIdle } from './utilities/run-when-idle.js'
import sessionStore from './stores/session-store.js'
import SessionService from './app/services/session-service.js'
import UserService from './app/services/user-service.js'
import partnerStore from './stores/partner-store.js'
import environment, { platform } from '../.configs/app/environment'
import { trackAppHistory } from './app/utils/app-history.js'
import {
    isIOSApp,
    registerNativeAuthListener,
} from './app/utils/native-auth.js'
import { isQaOverlayEnabledForCurrentEnvironment } from './app/utils/qa-dev-tools-gating.js'
import {
    AuthModalProvider,
    useAuthModal,
} from './app/contexts/auth-modal-context.jsx'
import {
    ContactSupportModalProvider,
    useContactSupportModal,
} from './app/contexts/contact-support-modal-context.jsx'
import { PartnerProvider } from './app/contexts/partner-context.jsx'
import { ConsentProvider } from './app/contexts/consent-context.jsx'
import {
    CONSENT_STATE,
    readStoredConsent,
} from './utilities/consent-storage.js'
import CookieBanner from './app/components/molecules/cookie-banner/cookie-banner.jsx'
// Both modals are only ever mounted behind a `<Show>` (never on first paint),
// so their component code is code-split out of the boot chunk via `lazy()`
// instead of being eagerly bundled — same pattern already used for e.g.
// `payment-methods-modal-row.jsx`.
const AuthModal = lazy(() => import('./app/modals/auth-modal/auth-modal'))
const ContactSupportModal = lazy(() => import('./app/modals/contact-support-modal/contact-support-modal.jsx'))
const ContactSupportSentToast = lazy(() => import('./app/modals/contact-support-modal/contact-support-sent-toast.jsx'))
// TENANT-5440: ToS & Privacy Policy consent gate. Rendered once here (global
// chrome, like AuthModal above) and driven by `legalAcceptanceStore`, so
// every code path that finds a stale acceptance (useVerificationGuard, the
// complete-profile flow) shares this single mount instead of wiring the
// modal per page.
const ConsentModal = lazy(() => import('./app/modals/consent-modal/consent-modal.jsx'))
// TENANT-5581 (QA C2): the scrim paints a spinner again, but only on routes that
// render none of their own. Lazy for the same bundle-discipline reason as the
// modals — anonymous visitors never render it.
const ConsentGateSpinner = lazy(() => import('./app/components/atoms/spinner-atom/spinner-atom-component.jsx'))
import legalAcceptanceStore from './stores/legal-acceptance-store.js'
import pageLoadingAffordanceStore from './stores/page-loading-affordance-store.js'
import {
    LocaleProvider,
    useLocale,
} from './app/contexts/locale-context.jsx'
// Only the (small) English messages are imported eagerly here. `loadMessages`
// (non-English) is dynamically imported from `./app/messages/index.jsx`
// below, only when a visitor actually switches locale — see that file's doc
// comment for why (TENANT-5036 bundle audit: its `import.meta.glob` mapping
// table is ~90KB minified and would otherwise ship in every English
// pageview's boot-critical chunk for no benefit).
import { enMessagesFinal as enMessages } from './app/messages/en-messages.js'
import { redirectToPreferredOrHostLocale } from './app/utils/locale-redirect.js'

// Before Solid mounts — host/preference locale redirect (e.g. lodgr.mx → /es-mx).
redirectToPreferredOrHostLocale()

// QA tooling (opt-in). Lazily imported so the overlay (and its `/dev/*` client)
// lives in its own chunk that is only fetched when rendered — gated by
// `isQaOverlayEnabledForCurrentEnvironment()` (see `App`).
const QaOverlay = lazy(() => import('./app/components/lodgr/lodgr-qa-overlay/lodgr-qa-overlay-root.jsx'))
function App () {
    const {
        bookingIntentToken,
        noClose: authModalNoClose,
        onboardingSource,
        showAuthModal,
        redirectUrl,
        closeAuthModal,
        variant,
        toSignUp,
    } = useAuthModal()
    const {
        bookingId: contactSupportBookingId,
        closeContactSupportModal,
        messageSent,
        notifyMessageSent,
        onBack: contactSupportOnBack,
        propertyId: contactSupportPropertyId,
        showContactSupportModal,
        source: contactSupportSource,
        subject: contactSupportSubject,
    } = useContactSupportModal()

    // TENANT-5440: persist the consent, then flip the store so the modal
    // hides and any flow awaiting `legalAcceptanceStore.require()` resumes.
    // On failure the modal stays up (it is non-dismissible) and the error
    // surfaces through the existing errorStore → ErrorModal pattern.
    // TENANT-5581 (QA D2): the scrim stopped the mouse and nothing else. Tab
    // walked into the page behind it and Enter activated what it found — the
    // header globe opened the locale menu while the page was otherwise blocked.
    // `inert` is the one thing that takes a whole subtree out of the tab order,
    // the accessibility tree and hit testing at once, so the gate sets it on the
    // app root for as long as it is up. The scrim and the consent modal both
    // render through Portals into <body>, outside #app, so they stay live.
    createEffect(() => {
        const appRoot = document.querySelector('#app')
        if (!appRoot) return

        const gateIsUp = legalAcceptanceStore.isPending() || legalAcceptanceStore.isRequired()
        if (gateIsUp) appRoot.setAttribute('inert', '')
        else appRoot.removeAttribute('inert')
    })

    // A gate left up when this component tears down would leave the whole app
    // inert with nothing on screen to explain it.
    onCleanup(() => document.querySelector('#app')?.removeAttribute('inert'))

    const handleLegalAcceptanceAccept = async () => {
        legalAcceptanceStore.setBusy(true)
        try {
            await UserService.recordLegalAcceptance()
            legalAcceptanceStore.markAccepted()
        } catch (error) {
            console.error('ConsentModal accept failed:', error)
            errorStore.addError(error, 'ConsentModal accept')
        } finally {
            legalAcceptanceStore.setBusy(false)
        }
    }

    return (
        <ErrorBoundary
            fallback={(error) => {
                console.error(error)
                // Buffer-aware: sends to Sentry now, or queues for flush if Sentry
                // is still loading (it's lazily code-split out of the boot bundle).
                // Guarded so a reporting failure can't stop errorStore.addError (the
                // app's user-facing error surface) from running below.
                try {
                    captureError(error)
                } catch (error_) {
                    console.warn('captureError failed:', error_)
                }
                errorStore.addError(error, 'Application UI error, cannot build')

                // Return a fallback UI
                return null
            }}
        >

            <PublicTemplateComponent>
                <For each={errorStore.getErrors()}>
                    {error => <ErrorModal error={error} onClose={() => errorStore.removeError(error)} />}
                </For>
            </PublicTemplateComponent>

            <Show when={showAuthModal()}>
                <AuthModal
                    bookingIntentToken={bookingIntentToken()}
                    noClose={authModalNoClose()}
                    onboardingSource={onboardingSource()}
                    onClose={closeAuthModal}
                    variant={variant()}
                    redirectUrl={redirectUrl()}
                    toSignUp={toSignUp()}
                />
            </Show>

            {/* TENANT-5440 QA fix: while an authenticated page's legal-
                acceptance status is UNKNOWN (useVerificationGuard's GET /user
                still in flight on a fresh page load), a full-viewport scrim
                blocks all interaction. The status resolving either drops the
                scrim (acceptance current) or swaps it for the consent modal
                in the same tick (stale) — the modal itself is never flashed
                speculatively. Anonymous pages never run the guard, so the
                scrim never renders for them.

                TENANT-5559: the scrim stopped painting a tint and a spinner of
                its own, because /properties, /stays and /bookings already render
                a loading state underneath it and the user saw two loaders at
                once on a hard refresh.

                TENANT-5581 (QA C2): that fix assumed every guarded route has a
                loading state. /referral-center and /property/new do not — they
                painted a finished-looking layout with empty fields while the
                gate was still blocking, and `cursor: progress` was the only cue.
                So the scrim asks rather than assumes: it paints only when
                nothing else on screen is already saying "not ready".

                TENANT-5581 (QA D2): the scrim blocked the mouse but not the
                keyboard — Tab reached the page behind it and Enter activated
                what it found. Blocking now comes from `inert` on the app root
                (see the effect above), which is why the scrim renders through a
                Portal: it sits outside the subtree it is switching off. */}
            <Show when={legalAcceptanceStore.isPending()}>
                <Portal>
                    <div
                        class="consent-gate-scrim"
                        classList={{
                            'consent-gate-scrim--visible': !pageLoadingAffordanceStore.isClaimed(),
                        }}
                        role="status"
                        aria-live="polite"
                    >
                        <Show when={!pageLoadingAffordanceStore.isClaimed()}>
                            <ConsentGateSpinner unclaimed={true} />
                        </Show>
                    </div>
                </Portal>
            </Show>

            <Show when={legalAcceptanceStore.isRequired()}>
                <ConsentModal
                    busy={legalAcceptanceStore.isBusy()}
                    onAccept={handleLegalAcceptanceAccept}
                />
            </Show>

            <CookieBanner />
            <Show when={isQaOverlayEnabledForCurrentEnvironment()}>
                <QaOverlay />
            </Show>
            <Show when={showContactSupportModal()}>
                <ContactSupportModal
                    bookingId={contactSupportBookingId()}
                    propertyId={contactSupportPropertyId()}
                    subject={contactSupportSubject()}
                    source={contactSupportSource()}
                    onBack={contactSupportOnBack()}
                    onClose={closeContactSupportModal}
                    onSubmitted={notifyMessageSent}
                />
            </Show>
            {/* Sits outside the modal so it survives the modal unmounting on
                a successful send. Gated like the modal above so the lazy chunk
                is fetched on first send rather than on every page load. */}
            <Show when={messageSent()}>
                <ContactSupportSentToast />
            </Show>
        </ErrorBoundary>
    )
}

// TODO: Consider merging this with the App component for simplicity.
function AppWithIntl () {
    const {
        locale,
    } = useLocale()

    // Lazy-load messages based on locale (English is cached, French loads on-demand)
    const [messages] = createResource(
        () => locale(),
        async (loc) => {
            try {
                if (loc === 'en') {
                    return enMessages
                }
                const {
                    loadMessages,
                } = await import('./app/messages/index.jsx')
                return await loadMessages(loc)
            } catch (error) {
                console.error('Error loading messages:', error)
                // Fallback to English on error
                return enMessages
            }
        },
    )

    // Remount the entire tree when messages finish loading for a new locale.
    const [
        intlGeneration,
        setIntlGeneration,
    ] = createSignal(1)
    createEffect(on(
        () => messages(),
        () => setIntlGeneration(generation => generation + 1),
        {
            defer: true,
        },
    ))

    return (
        <Show when={intlGeneration()} keyed>
            {(_generation) => (
                <IntlProvider locale={locale()} messages={messages() || enMessages}>
                    <AuthModalProvider>
                        <ContactSupportModalProvider>
                            <App />
                        </ContactSupportModalProvider>
                    </AuthModalProvider>
                </IntlProvider>
            )}
        </Show>
    )
}

// Initialize PostHog at module scope — before render/mount — so the feature
// flags request is in flight while the component tree is still building. This
// shrinks the hero experiment's first-visit skeleton window to roughly the
// network round-trip alone. Honor stored consent at init time so PostHog never
// captures between `posthog.init()` and the `ConsentProvider.onMount` opt-out
// for users who previously declined. ConsentProvider still re-asserts opt-out
// defensively (handles cases where the SDK's own persisted opt-out state was
// cleared while ours survived — e.g. browser extensions).
initPostHog({
    optOutByDefault: readStoredConsent() === CONSENT_STATE.REJECTED,
})

// Mobile app only: the native shell boots at `/`, which on web is the marketing
// homepage — a surface the app deliberately does not include. Rewrite the URL to
// the app's real landing surface before the router initialises, so the homepage
// is never rendered and there is no visible flip.
//
// Only an exact `/` is rewritten. Deep links arriving on a real path (a shared
// listing, the OAuth callback) must be left alone.
if (platform.isMobileApp) {
    const bootPath = globalThis.location.pathname
    if (bootPath === '/' || bootPath === '/index.html') {
        globalThis.history.replaceState(
            undefined,
            '',
            `/search${globalThis.location.search}${globalThis.location.hash}`,
        )
    }
}

// TENANT-5315: catch the login return. Authorization runs in the device
// browser, so Core Auth's callback arrives as an App Link that foregrounds the
// app rather than as a navigation inside it — there is no page load to hook.
// Registered before render so a link that launches the app cold is not missed.
if (platform.isMobileApp) {
    registerNativeAuthListener((landingPath) => {
        globalThis.location.assign(landingPath)
    })
}

// iOS: the web view runs edge to edge, under the status bar and the home
// indicator, like a native screen. It used to be inset below the status bar
// by Capacitor (`ios.contentInset: always`), but a scroll-view inset makes
// every `position: fixed` element slide under the status bar as soon as the
// page scrolls — on our pages and on every page the gateway serves. So the
// inset is gone: the viewport declares `viewport-fit=cover`, which is what
// makes `env(safe-area-inset-*)` report the real bars, and the app's own
// chrome pads itself with those values under `html.ios-app` (the header, the
// modal backdrops, the Search page's edit sheet and Map pill). The website
// keeps its own viewport meta: Safari's safe areas are its own business.
if (isIOSApp()) {
    document.documentElement.classList.add('ios-app')
    document.querySelector('meta[name="viewport"]').setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover')
}

// The app's real viewport height, for layouts sized with
// `var(--app-viewport-height, 100vh)` (the Search page, the public template).
// `window.innerHeight` is the visible height on every platform the app runs
// on; the website never sets the variable and keeps its `100vh`.
if (platform.isMobileApp) {
    const setViewportHeight = () => {
        document.documentElement.style.setProperty('--app-viewport-height', `${globalThis.innerHeight}px`)
    }
    setViewportHeight()
    globalThis.addEventListener('resize', setViewportHeight)
}

// The app's history depth, for the header's back control (see
// app-history.js). Installed before the router mounts so its first
// navigation is counted too.
if (platform.isMobileApp) {
    trackAppHistory()
}

// Internal links that open "in a new tab" (`target="_blank"`: the Search
// cards, Terms and Privacy in the modals, FAQ links). Capacitor Android loads
// them in the same WebView; Capacitor iOS hands them to the system, which
// cannot open the app's own capacitor:// address, so on iOS they did nothing.
// Same-origin ones navigate in place here; external links keep their native
// handling (Safari on iOS).
if (platform.isMobileApp) {
    document.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return
        const anchor = event.target?.closest?.('a[target="_blank"][href]')
        if (!anchor || anchor.origin !== globalThis.location.origin) return
        event.preventDefault()
        globalThis.location.assign(anchor.href)
    })
}

render(() => {
    onMount(() => {
        // Sentry is not needed for first paint, FCP, or LCP, so its init (and
        // the ~620KB `@sentry/solid` chunk it dynamically imports) is deferred
        // until the user interacts with the page or a fixed backstop timeout
        // elapses (see `runWhenIdle`'s doc comment for why a plain
        // `requestIdleCallback` isn't enough here), same as the analytics
        // block below. This is safe: the early-error buffer set up in
        // `index.html` already queues any error thrown before Sentry finishes
        // loading — that buffer now just covers a slightly longer (still
        // capped) window — and `initSentry()` flushes it in full once ready,
        // so no error is lost, only delayed.
        runWhenIdle(() => initSentry())

        // Auth/session sync and extension-installation detection are kept on
        // the immediate boot path (not deferred to idle below). Both are
        // already non-blocking (async I/O, no heavy synchronous work), so
        // deferring them would not meaningfully help TBT/FCP, but it WOULD
        // delay `useAuthSync`'s cross-tab storage/message listeners and the
        // extension auth handshake — a logged-in user's authenticated header
        // state should resolve as soon as possible, just not before first
        // paint (which it already doesn't block, since nothing here is
        // awaited synchronously).
        partnerStore.capturePartnerCodeFromUrl()
        useAuthSync()
        ensureLodgrExtensionInstallationDetected()
            .then(async (isInstalled) => {
                if (!isInstalled) return
                const extensionToken = await requestExtensionAuth()
                if (!extensionToken) return
                const currentToken = await sessionStore.getAccessToken()
                if (!currentToken) {
                    await sessionStore.setAccessToken(extensionToken)
                    await SessionService.checkAuthentication()
                    // The auth signal flip above is picked up by the identify
                    // effect; no inline identify needed.
                }
            })
            .catch((error) => {
                console.warn('App extension detection failed:', error)
            })

        // Analytics/tracking (PostHog, GA4, Reddit/Meta pixels): none of this
        // is needed for first paint, FCP, or LCP, so it's deferred until the
        // user interacts with the page (pointerdown/scroll/keydown/touchstart)
        // or a fixed backstop timeout elapses — never merely "the main thread
        // is idle", since that happens almost immediately on quiet marketing
        // pages and would still land inside Lighthouse's TBT trace window
        // (capped fallback timeout so it still always eventually runs, even
        // for a bot/crawler or a very short session with no interaction).
        // This keeps the boot-critical path free of the PostHog SDK's own
        // parse/init cost, on top of it already being code-split out of the
        // boot chunk (see `initPostHog`/`use-posthog.js`).
        const owner = getOwner()
        runWhenIdle(() => runWithOwner(owner, () => {
            // Read stored consent HERE (not hoisted above the idle defer) so
            // a user who accepts/declines the cookie banner while this is
            // still pending gets the up-to-date choice baked into the very
            // first `posthog.init()` call — see the comment on
            // `initPostHog` in `use-posthog.js` for why this is still race-free
            // even though `ConsentContext`'s optIn/optOut calls made before
            // PostHog finishes loading are otherwise no-ops.
            const storedConsent = readStoredConsent()
            initPostHog({
                optOutByDefault: storedConsent === CONSENT_STATE.REJECTED,
            })

            // Single source of truth for PostHog identity. Reacts to
            // `SessionService.isAuthenticated`, which flips on email login, OAuth
            // landing (any page that ends up calling `SessionService.checkAuthentication`),
            // page refresh while signed in, extension auth push, and cross-tab login.
            // No per-controller identify calls needed — this hook covers them all.
            // `posthog.reset()` is fired separately from `SessionService.delete` /
            // `logout` / `logoutAndNotify` and from the cross-tab logout branch in
            // `useAuthSync`, so this effect only handles the false→true direction.
            //
            // We decode `user_id` from the JWT rather than from `GET /user` because
            // the API's UserResponse schema currently omits `user_id` (see the
            // detailed comment on `decodeJwtPayload` in `use-posthog.js`). When
            // that API spec drift is fixed, this effect can be simplified back to
            // a single `UserService.show()` call that reads `user.user_id` directly.
            let lastIdentifiedUserId = null
            createEffect(async () => {
                if (!SessionService.isAuthenticated()) {
                    lastIdentifiedUserId = null
                    return
                }
                try {
                    const token = await sessionStore.getAccessToken()
                    if (!token) return
                    const payload = decodeJwtPayload(token)
                    const userId = payload?.user_id
                    if (!userId || userId === lastIdentifiedUserId) return
                    // Identify immediately with the user_id from the JWT so the
                    // PostHog session attributes correctly even if the /user call
                    // below is slow, fails, or returns an unexpected shape.
                    posthogIdentify(userId)
                    lastIdentifiedUserId = userId
                } catch (error) {
                    captureError(error)
                    return
                }

                // Background enrichment: fetch /user for email-derived person
                // properties (currently `is_company_email`). Failure here must not
                // disturb the identify above — we already have a stable identity.
                try {
                    const user = await UserService.show()
                    const properties = personPropertiesFromUser(user)
                    if (Object.keys(properties).length > 0) {
                        posthogSetPersonProperties(properties)
                    }
                } catch (error) {
                    captureError(error)
                }
            })

            // The website only. Apple counts these ad and measurement tags as
            // tracking, which an app may do only after the App Tracking
            // Transparency prompt; the app has none and its App Privacy answers
            // declare no tracking. PostHog above stays, behind the cookie banner.
            if (platform.isWeb) {
                initGtag(environment.app.gaMeasurementId)
                initRedditPixel()
                initMetaPixel()
            }
        }))
    })

    return (
        <Router>
            <LocaleProvider>
                <PartnerProvider>
                    <ConsentProvider>
                        <AppWithIntl />
                    </ConsentProvider>
                </PartnerProvider>
            </LocaleProvider>
        </Router>
    )
}, app)
